import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";

// region Imports - External libraries
import { TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
// endregion Imports - External libraries
// region Imports - Hooks
import useTranslation from "src/translations/useTranslation";
import { useAuth } from "@hooks/useAuth";
import { saveGoogleApiUsageLog } from "@hooks/useGoogleApiUsageLog";
// endregion Imports - Hooks
// region Imports - Shared / Services
import { MapMessages } from "@shared/languages/interfaces";
import { GoogleApiRequestTypes } from "@shared/constants/google-api-request-types.enum";
import {
  parseCoordinates,
  PlaceSearchResult,
  PlaceSearchSession,
  PlaceSearchSuggestion
} from "@services/googleMaps/placeSearch";
// endregion Imports - Shared / Services

/** Characters typed before searching */
const MIN_SEARCH_LENGTH = 3;
/** Wait after the last key before searching */
const SEARCH_DEBOUNCE_MS = 300;

type PlaceSearchFieldProps = {
  /** Id of the input (kept from the old widget, the screens style it) */
  id: string,
  label: string,
  placeholder?: string,
  /** Map whose visible area ranks the suggestions */
  map?: google.maps.Map,
  /** Called with the chosen place, or with the coordinates typed as "lat, lng" + Enter */
  onSelect: (result: PlaceSearchResult) => void,
  /** Change the value to clear the field */
  clearSignal?: number,
  margin?: "none" | "dense" | "normal",
  fullWidth?: boolean
}

/**
 * Address search box for the maps: suggestions from Google Places (new API, legacy as fallback),
 * and typed coordinates ("lat, lng" + Enter) without calling Google.
 */
const PlaceSearchField: React.FC<PlaceSearchFieldProps> = (
  { id, label, placeholder, map, onSelect, clearSignal, margin, fullWidth }
) => {

  const { t } = useTranslation();
  const { user } = useAuth();

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<PlaceSearchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const sessionRef = useRef<PlaceSearchSession | null>(null);
  const requestIdRef = useRef(0);
  const userRef = useRef(user);

  userRef.current = user;

  /** Current search session, opening (and logging) a new one when needed */
  const getSession = useCallback((): PlaceSearchSession => {
    if (!sessionRef.current) {
      sessionRef.current = new PlaceSearchSession();
      saveGoogleApiUsageLog(GoogleApiRequestTypes.PLACES_AUTOCOMPLETE_SESSION, userRef.current);
    }

    return sessionRef.current;
  }, []);

  const search = useMemo(() => _.debounce(async (text: string) => {

    requestIdRef.current += 1;

    const requestId = requestIdRef.current;

    try {
      const suggestions = await getSession().fetchSuggestions(text, map?.getBounds() ?? null);

      if (requestId === requestIdRef.current) {
        setOptions(suggestions);
        setFailed(false);
      }
    } catch (error) {
      console.log(error);
      if (requestId === requestIdRef.current) {
        setOptions([]);
        setFailed(true);
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }

  }, SEARCH_DEBOUNCE_MS), [map, getSession]);

  // Stop a pending search when the field leaves the screen
  useEffect(() => () => search.cancel(), [search]);

  // Clear the field when the screen asks (e.g. a click on the map)
  useEffect(() => {
    if (!clearSignal) return;

    search.cancel();
    requestIdRef.current += 1;
    setInputValue("");
    setOptions([]);
    setLoading(false);
  }, [clearSignal, search]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI Autocomplete event
  const handleInputChange = useCallback((_event: any, value: string, reason: string) => {

    setInputValue(value);

    // The field is filled with the chosen suggestion: nothing to search
    if (reason === "reset") return;

    setFailed(false);

    if (value.trim().length < MIN_SEARCH_LENGTH || parseCoordinates(value)) {
      search.cancel();
      requestIdRef.current += 1;
      setOptions([]);
      setLoading(false);

      return;
    }

    setLoading(true);
    search(value.trim());

  }, [search]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MUI Autocomplete event
  const handleChange = useCallback(async (_event: any, value: PlaceSearchSuggestion | string | null) => {

    if (!value) return;

    // Free text + Enter: only coordinates are accepted, as in the old search box
    if (typeof value === "string") {
      const coordinates = parseCoordinates(value);

      if (coordinates) onSelect({ location: coordinates, formattedAddress: value.trim() });

      return;
    }

    try {
      setLoading(true);
      const result = await getSession().fetchPlace(value);

      saveGoogleApiUsageLog(GoogleApiRequestTypes.PLACES_PLACE_DETAILS, userRef.current);
      sessionRef.current = null;

      if (result) onSelect(result);
    } catch (error) {
      console.log(error);
      setFailed(true);
    } finally {
      setLoading(false);
    }

  }, [getSession, onSelect]);

  return (
    <Autocomplete
      id={id}
      freeSolo
      options={options}
      filterOptions={(items) => items}
      getOptionLabel={(option: PlaceSearchSuggestion | string) => (typeof option === "string" ? option : option.label)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      loading={loading}
      loadingText={t(MapMessages.addressSearchLoading)}
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          margin={margin}
          fullWidth={fullWidth}
          error={failed}
          helperText={failed ? t(MapMessages.addressSearchUnavailable) : undefined}
        />
      )}
    />
  );
};

export default PlaceSearchField;
